import React, {useState} from 'react';
import styled from 'styled-components';
import PreviewErrorState from './PreviewErrorState';
import {resolveTemplate} from './resolver/resolveTemplate';
import type {MergeContext, QuoteTemplate} from './resolver/types';
import QuotePreview from './preview/QuotePreview';

type Props = {
	template: QuoteTemplate;
	mergeContext: MergeContext;
};

const Scroller = styled.div`
	flex: 1;
	min-width: 0;
	min-height: 0;
	overflow-y: auto;
	padding: 24px 16px;
	box-sizing: border-box;
`;

class PreviewErrorBoundary extends React.Component<
	{fallback: React.ReactNode; resetKey: number; children: React.ReactNode},
	{errored: boolean}
> {
	static getDerivedStateFromError() {
		return {errored: true};
	}
	state = {errored: false};
	componentDidUpdate(prev: {resetKey: number}) {
		if (prev.resetKey !== this.props.resetKey && this.state.errored) {
			this.setState({errored: false});
		}
	}
	render() {
		if (this.state.errored) return this.props.fallback;
		return this.props.children;
	}
}

/**
 * The live preview renders the document as HTML (the same pages and computed content the PDF
 * export produces), so every settings change repaints in place: no PDF regeneration while
 * editing, no viewer reload, and the scroll position survives edits. The actual PDF is only
 * built on Download and Publish.
 */
const PreviewPane: React.FC<Props> = ({template, mergeContext}) => {
	const [retryKey, setRetryKey] = useState(0);
	const resolved = resolveTemplate(template, mergeContext);
	return (
		<Scroller data-cy="quote-preview-scroller">
			<PreviewErrorBoundary resetKey={retryKey} fallback={<PreviewErrorState onRetry={() => setRetryKey(k => k + 1)} />}>
				<QuotePreview data={mergeContext.data} template={resolved} />
			</PreviewErrorBoundary>
		</Scroller>
	);
};

export default PreviewPane;
